import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import makeComponentTrashable from 'trashable-react';
import { fetchReceivablesInvoices } from 'Shared/util/web-api';
import { formatDate, formatAmount } from 'Shared/util/formatters';
import Page from 'Shared/components/page/page';
import { ColumnTypes } from 'Shared/components/tables/data-table';
import StatusMessage from 'Shared/components/status-message';
import { exportPage } from 'Shared/util/export-helper';
import ExportOptions from 'Shared/components/export-options';
import { useStatusMessage, useTranslation } from 'Shared/util/custom-hooks';
import { translateColumns } from 'Shared/util/translations';
import { RadioGroup, RadioButton } from 'Shared/components/radio';
import filterStyles from 'Shared/components/list-filter.css';
import Datepicker from 'Shared/components/datepicker';
import useTranslationContext from 'Contexts/translation-context';
import BasicInvoiceTable from './basic-invoice-table';
import InvoiceFilter, * as filterStates from './invoice-filter';



const columns = [{
  title: 'invoiceNumber',
  key: 'number',
}, {
  title: 'issuer',
  key: 'debtor',
}, {
  title: 'invoiceDate',
  key: 'invoiceDate',
  type: ColumnTypes.Date,
  formatter: (d) => formatDate(d),
}, {
  title: 'dueDate',
  key: 'dueDate',
  type: ColumnTypes.Date,
  formatter: (d) => formatDate(d),
  hiddenMobile: true,
}, {
  title: 'totalAmount',
  key: 'totalAmount',
  type: ColumnTypes.Amount,
  formatter: (d) => formatAmount(d),
  footer: {
    sum: true,
  },
}, {
  title: 'balance',
  key: 'remainingAmount',
  type: ColumnTypes.Amount,
  formatter: (d) => formatAmount(d),
  footer: {
    sum: true,
  },
  hiddenMobile: true,
}, {
  title: 'currency',
  key: 'currencyCode',
  footer: {
    groupBy: true,
  },
}, {
  title: 'status',
  key: 'status',
}];


const InvoicesReceivablesPage = ({
  registerPromise,
}) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, dispatchStatusMessage] = useStatusMessage();
  const t = useTranslation(x => x.invoices);
  const tg = useTranslation(x => x.global);
  const { translationData } = useTranslationContext();

  useEffect(() => {
    dispatchStatusMessage({ type: 'NONE' });
    registerPromise(fetchReceivablesInvoices())
      .then((result) => {
        setInvoices(result);
        setLoading(false);
      });
  }, []);

  return (
    <Page title={t.myDebt}>
      <InvoiceFilter
        default={{
          state: filterStates.UNPAYED,
          fromDate: null,
          toDate: null,
        }}
        items={invoices}
        dismissExportAlert={() => dispatchStatusMessage({ type: 'NONE' })}
        render={(filterArgs) => (
          <>
            <div className={filterStyles.filterItemsContainer}>
              <div className={`${filterStyles.filterItem} ${filterStyles.filterDatesContainer}`}>
                <Datepicker
                  selected={filterArgs.filter.fromDate}
                  onChange={(date) => filterArgs.handleChange({ fromDate: date })}
                  label={tg.invoiceDate}
                  placeholder={tg.from}
                  language={translationData.languageCode}
                />
                <Datepicker
                  selected={filterArgs.filter.toDate}
                  onChange={(date) => filterArgs.handleChange({ toDate: date })}
                  placeholder={tg.to}
                  language={translationData.languageCode}
                />
              </div>
              <div className={filterStyles.filterItem}>
                <RadioGroup
                  name="invoice-filter"
                  value={filterArgs.filter.state}
                  handleChange={(e) => filterArgs.handleChange({ state: e.target.value })}
                >
                  <RadioButton value={filterStates.ALL} text={tg.all} />
                  <RadioButton value={filterStates.UNPAYED} text={t.unpaid} />
                  <RadioButton value={filterStates.PAYED} text={tg.paid} />
                </RadioGroup>
              </div>
            </div>
            <ExportOptions exportPage={exportPage.RECEIVABLES} filterArgs={filterArgs} dispatchStatusMessage={dispatchStatusMessage} />
            <StatusMessage status={statusMessage.type} text={statusMessage.message} clearStatus={() => dispatchStatusMessage({ type: 'NONE' })} />
            <BasicInvoiceTable
              rows={filterArgs.items}
              columns={translateColumns(columns, tg)}
              loading={loading}
              filterProperties={['status', 'statusAddition']}
            />
          </>
        )}
      />
    </Page>
  );
};

InvoicesReceivablesPage.propTypes = {
  registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(InvoicesReceivablesPage);
